import { useEffect, useRef, useState } from 'preact/hooks';
import { t } from '../../i18n/index.js';
import { SNAKE_H, SNAKE_W, type SnakeDir } from '../../../shared/games/snake/index.js';

export type SnakeTheme = 'lcd' | 'neon';

const THEME_KEY = 'areena.snakeTheme';

/** The chosen look (decision 2026-09-30: retro LCD or Areena neon), remembered in this browser. */
export function useSnakeTheme(): [SnakeTheme, (t: SnakeTheme) => void] {
  const [theme, setTheme] = useState<SnakeTheme>(() => {
    try {
      return localStorage.getItem(THEME_KEY) === 'neon' ? 'neon' : 'lcd';
    } catch {
      return 'lcd';
    }
  });
  const choose = (next: SnakeTheme) => {
    setTheme(next);
    try {
      localStorage.setItem(THEME_KEY, next);
    } catch {
      // Not stored (private window): the choice lasts for this page.
    }
  };
  return [theme, choose];
}

export interface BoardSnake {
  /** Cells as y * SNAKE_W + x, head first. */
  body: number[];
  dir: SnakeDir;
  alive: boolean;
  mine: boolean;
}

const CELL = 20;
const WIDTH = SNAKE_W * CELL;
const HEIGHT = SNAKE_H * CELL;

const LCD = { bg: '#c7d8a0', grid: 'rgba(40, 60, 20, 0.07)', ink: '#2b3a1a', inkSoft: 'rgba(43, 58, 26, 0.55)' };
const NEON = { bg: '#070b16', grid: 'rgba(92, 200, 255, 0.06)', mine: '#3ddc97', theirs: '#ff5ca8', apple: '#ffd166' };

/** Canvas board of the snake game. Drawn again on every change of the state. */
export function SnakeBoard(props: { snakes: BoardSnake[]; apples: number[]; theme: SnakeTheme; dim?: boolean }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const ctx = canvas.current?.getContext('2d');
    if (!ctx) return;
    if (props.theme === 'lcd') drawLcd(ctx, props.snakes, props.apples);
    else drawNeon(ctx, props.snakes, props.apples);
  });
  return (
    <div class={`snake-screen snake-${props.theme} ${props.dim ? 'dim' : ''}`}>
      <canvas ref={canvas} width={WIDTH} height={HEIGHT} role="img" aria-label={t.snake.boardLabel} />
    </div>
  );
}

const xy = (k: number) => ({ x: k % SNAKE_W, y: Math.floor(k / SNAKE_W) });

function drawLcd(ctx: CanvasRenderingContext2D, snakes: BoardSnake[], apples: number[]) {
  ctx.fillStyle = LCD.bg;
  ctx.fillRect(0, 0, WIDTH, HEIGHT);
  // Faint pixel grid of the LCD.
  ctx.fillStyle = LCD.grid;
  for (let y = 0; y < SNAKE_H; y++) for (let x = 0; x < SNAKE_W; x++) ctx.fillRect(x * CELL + 1, y * CELL + 1, CELL - 2, CELL - 2);
  for (const a of apples) {
    const { x, y } = xy(a);
    // A small "plus" of pixels like the original apple.
    ctx.fillStyle = LCD.ink;
    const c = CELL / 4;
    ctx.fillRect(x * CELL + c * 1.5, y * CELL + c * 0.5, c, c);
    ctx.fillRect(x * CELL + c * 0.5, y * CELL + c * 1.5, c, c);
    ctx.fillRect(x * CELL + c * 2.5, y * CELL + c * 1.5, c, c);
    ctx.fillRect(x * CELL + c * 1.5, y * CELL + c * 2.5, c, c);
  }
  for (const s of snakes) {
    s.body.forEach((k, i) => {
      const { x, y } = xy(k);
      const px = x * CELL;
      const py = y * CELL;
      ctx.fillStyle = s.alive ? LCD.ink : LCD.inkSoft;
      if (s.mine || i === 0) {
        ctx.fillRect(px + 1, py + 1, CELL - 2, CELL - 2);
      } else {
        // The opponent: hollow segments (the head is filled).
        ctx.fillRect(px + 1, py + 1, CELL - 2, CELL - 2);
        ctx.fillStyle = LCD.bg;
        ctx.fillRect(px + 5, py + 5, CELL - 10, CELL - 10);
      }
      if (i === 0) drawEyes(ctx, px, py, s.dir, LCD.bg);
    });
  }
}

function drawNeon(ctx: CanvasRenderingContext2D, snakes: BoardSnake[], apples: number[]) {
  ctx.fillStyle = NEON.bg;
  ctx.fillRect(0, 0, WIDTH, HEIGHT);
  ctx.strokeStyle = NEON.grid;
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let x = 1; x < SNAKE_W; x++) {
    ctx.moveTo(x * CELL + 0.5, 0);
    ctx.lineTo(x * CELL + 0.5, HEIGHT);
  }
  for (let y = 1; y < SNAKE_H; y++) {
    ctx.moveTo(0, y * CELL + 0.5);
    ctx.lineTo(WIDTH, y * CELL + 0.5);
  }
  ctx.stroke();
  for (const a of apples) {
    const { x, y } = xy(a);
    ctx.save();
    ctx.shadowColor = NEON.apple;
    ctx.shadowBlur = 14;
    ctx.fillStyle = NEON.apple;
    ctx.beginPath();
    ctx.arc(x * CELL + CELL / 2, y * CELL + CELL / 2, CELL * 0.32, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
  for (const s of snakes) {
    const color = s.mine ? NEON.mine : NEON.theirs;
    ctx.save();
    ctx.globalAlpha = s.alive ? 1 : 0.35;
    ctx.shadowColor = color;
    ctx.shadowBlur = 10;
    const n = s.body.length;
    s.body.forEach((k, i) => {
      const { x, y } = xy(k);
      // Fades a little towards the tail.
      ctx.globalAlpha = (s.alive ? 1 : 0.35) * (1 - (i / Math.max(1, n)) * 0.45);
      ctx.fillStyle = color;
      roundRect(ctx, x * CELL + 2, y * CELL + 2, CELL - 4, CELL - 4, i === 0 ? 6 : 4);
      if (i === 0) {
        ctx.shadowBlur = 0;
        drawEyes(ctx, x * CELL, y * CELL, s.dir, NEON.bg);
        ctx.shadowBlur = 10;
      }
    });
    ctx.restore();
  }
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.fill();
}

/** Two eye pixels on the head, towards the moving direction. */
function drawEyes(ctx: CanvasRenderingContext2D, px: number, py: number, dir: SnakeDir, color: string) {
  ctx.fillStyle = color;
  const s = 3;
  const near = 5;
  const far = CELL - 5 - s;
  const pos: Record<SnakeDir, [number, number][]> = {
    up: [[near, near], [far, near]],
    down: [[near, far], [far, far]],
    left: [[near, near], [near, far]],
    right: [[far, near], [far, far]],
  };
  for (const [ex, ey] of pos[dir]) ctx.fillRect(px + ex, py + ey, s, s);
}

const KEYS: Record<string, SnakeDir> = {
  ArrowUp: 'up',
  ArrowDown: 'down',
  ArrowLeft: 'left',
  ArrowRight: 'right',
  w: 'up',
  s: 'down',
  a: 'left',
  d: 'right',
  W: 'up',
  S: 'down',
  A: 'left',
  D: 'right',
};

/**
 * Keyboard (arrows, WASD) and swipes on the given element. Keys typed into a text field (the
 * match chat) are not turns.
 */
export function useSnakeControls(onTurn: (dir: SnakeDir) => void, swipeArea: { current: HTMLElement | null }, enabled: boolean) {
  const turn = useRef(onTurn);
  turn.current = onTurn;
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      const dir = KEYS[e.key];
      if (!dir || e.ctrlKey || e.metaKey || e.altKey) return;
      e.preventDefault();
      turn.current(dir);
    };
    addEventListener('keydown', onKey);
    const el = swipeArea.current;
    let start: { x: number; y: number } | null = null;
    const onStart = (e: TouchEvent) => {
      const p = e.touches[0];
      if (p) start = { x: p.clientX, y: p.clientY };
    };
    const onMove = (e: TouchEvent) => {
      const p = e.touches[0];
      if (!start || !p) return;
      e.preventDefault(); // no page scrolling while steering
      const dx = p.clientX - start.x;
      const dy = p.clientY - start.y;
      if (Math.max(Math.abs(dx), Math.abs(dy)) < 18) return;
      turn.current(Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 'right' : 'left') : dy > 0 ? 'down' : 'up');
      start = { x: p.clientX, y: p.clientY };
    };
    const onEnd = () => {
      start = null;
    };
    el?.addEventListener('touchstart', onStart, { passive: true });
    el?.addEventListener('touchmove', onMove, { passive: false });
    el?.addEventListener('touchend', onEnd);
    return () => {
      removeEventListener('keydown', onKey);
      el?.removeEventListener('touchstart', onStart);
      el?.removeEventListener('touchmove', onMove);
      el?.removeEventListener('touchend', onEnd);
    };
  }, [enabled]);
}

/** On-screen arrow buttons (phones). */
export function SnakePad(props: { onTurn: (dir: SnakeDir) => void; disabled?: boolean }) {
  const button = (dir: SnakeDir, symbol: string) => (
    <button
      type="button"
      class={`snake-pad-btn snake-pad-${dir}`}
      aria-label={t.snake.controls[dir]}
      disabled={props.disabled}
      onPointerDown={(e) => {
        e.preventDefault();
        props.onTurn(dir);
      }}
    >
      {symbol}
    </button>
  );
  return (
    <div class="snake-pad">
      {button('up', '▲')}
      {button('left', '◀')}
      {button('right', '▶')}
      {button('down', '▼')}
    </div>
  );
}

export function ThemeSwitch(props: { theme: SnakeTheme; onChange: (t: SnakeTheme) => void }) {
  return (
    <div class="snake-theme" role="group" aria-label={t.snake.theme}>
      <span class="muted">{t.snake.theme}:</span>
      {(['lcd', 'neon'] as const).map((th) => (
        <button type="button" class={`btn btn-small ${props.theme === th ? 'btn-primary' : ''}`} aria-pressed={props.theme === th} onClick={() => props.onChange(th)}>
          {t.snake.themes[th]}
        </button>
      ))}
    </div>
  );
}
